// frontend/src/hooks/useLeagueDetails.ts
import { useQuery } from '@tanstack/react-query';
import { fetchLeagueDetails } from '@/api/leagues';
import { useAuth } from '@/contexts/AuthContext';
import { LeagueRead } from '@/api/schemas';
import { ApiError } from '@/api/auth';

/**
 * Custom hook to fetch detailed information for a specific season-long league.
 * It encapsulates the data-fetching logic, caching, and state management
 * provided by TanStack Query.
 *
 * @param leagueId The numeric ID of the league to fetch. Can be null if not yet available.
 * @returns The query object from TanStack Query, which includes:
 *  - `data`: The fetched LeagueRead object.
 *  - `isLoading`: Boolean, true while the initial fetch is in progress.
 *  - `isError`: Boolean, true if the fetch resulted in an error.
 *  - `error`: The error object if isError is true.
 *  - `isFetching`: Boolean, true if a background refetch is in progress.
 *  - `refetch`: A function to manually trigger a refetch.
 */
export function useLeagueDetails(leagueId: number | null) {
  const { token } = useAuth();

  // The useQuery hook from TanStack Query
  return useQuery<LeagueRead, ApiError>({ // Explicitly type the data and error for type safety
    // The Query Key is a unique identifier for this data in the cache.
    // It's an array, and any change to its elements will trigger a refetch.
    // Here, it's tied to the specific leagueId.
    queryKey: ['leagueDetails', leagueId],

    // The Query Function is the async function that performs the data fetching.
    // It will only be executed if the `enabled` option below is true.
    queryFn: async () => {
      // We can assert non-null here because `enabled` check ensures they exist.
      return fetchLeagueDetails(leagueId!, token!);
    },

    // This is a powerful option that prevents the query from running
    // until all necessary dependencies (leagueId and token) are available.
    enabled: !!leagueId && !!token,

    // Optional but recommended: Configure how long data is considered "fresh".
    // After this time, it becomes "stale" and will be refetched in the background
    // on the next component mount or window focus.
    staleTime: 1000 * 60 * 5, // 5 minutes

  });
}